import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import PersonalInfo from 'components/application/PersonalInfo';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import PersonnelCourseTrainingStatusIndicator from 'components/personnel/PersonnelCourseTrainingStatusIndicator';
import PersonnelLabel from 'components/personnel/PersonnelLabel';

import { asFormattedDateTime, formatDate } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function BookingSidePanelDetailsTab({
  booker,
  bookingGroupInfoBanner,
  course,
  domainBookingSource,
  domainBookingSourcePersonnelCollection,
  endOfListRef,
  isFetching,
  shouldShowPersonnel = false,
  totalReadableBookingCount,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.BookingSidePanelDetailsTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const hasPersonnelAccessType =
    currentActor.user.attributes.accessType === 'personnel';

  const { createdAt, date, notes } = domainBookingSource?.attributes || {};

  const bookerText = (() => {
    if (!booker) return null;

    const { deletedAt, email } = booker.attributes;

    return hasPersonnelAccessType ?
        <>
          <span>
            {personDisplayName({ attributes: booker.attributes })}
            {deletedAt && ` ${i18nTCommon('shared.deletedUser')}`}
          </span>
          {!deletedAt && (
            <>
              {' '}
              <span>&#40;</span>
              <a
                className='app-link tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                href={`mailto:${email}`}
              >
                {email}
              </a>
              <span>&#41;</span>
            </>
          )}
        </>
      : i18nT('booker.text', {
          bookerName: [
            personDisplayName({ attributes: booker.attributes }),
            deletedAt && i18nTCommon('shared.deletedUser'),
          ]
            .filter(Boolean)
            .join(' '),
          dateTime: createdAt && asFormattedDateTime({ date: createdAt }),
          interpolation: { escapeValue: false },
        });
  })();

  const courseHasELearningCourse =
    !!course.relationships.eLearningCourse.data?.id;

  return (
    <>
      <div className='popup__body-content popup__body-content--border-top tw-border-grey-100'>
        {!courseHasELearningCourse && (
          <>
            <PersonalInfo
              label={i18nTCommon('training.trainingDate')}
              text={date && formatDate({ date, format: 'dd MMM yy' })}
            />
            <PersonalInfo
              isTruncated={false}
              label={i18nTCommon('bookings.bookingNotes')}
              text={notes}
            />
          </>
        )}
        <PersonalInfo
          label={i18nT(
            `booker.label.${courseHasELearningCourse ? 'eLearning' : 'default'}`,
          )}
          text={bookerText}
        />
        {!hasPersonnelAccessType &&
          !shouldShowPersonnel &&
          bookingGroupInfoBanner}
      </div>
      {shouldShowPersonnel && (
        <div className='popup__body-content popup__body-content--border-top tw-border-grey-100'>
          <div className='tw-flex'>
            <div className='personal-info__label tw-mb-2 tw-mr-2 tw-font-medium tw-text-grey-900'>
              {i18nTCommon('shared.personnel')}
            </div>
            <div className='tw-flex tw-h-5 tw-items-center tw-justify-center tw-rounded-lg tw-bg-grey-050 tw-px-1.5 tw-text-center'>
              <div className='tw-m-0 tw-font-inter tw-text-xs tw-font-semibold'>
                {totalReadableBookingCount}
              </div>
            </div>
          </div>
          <div className='tw-mb-2'>{bookingGroupInfoBanner}</div>
          <div className='tw-flex tw-flex-col tw-gap-1'>
            {domainBookingSourcePersonnelCollection?.map((personnel) => (
              <PersonnelLabel key={personnel.id} personnel={personnel} />
            ))}
            {isFetching && (
              <div className='tw-flex tw-h-8 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-grey-050 tw-px-3'>
                <PersonnelCourseTrainingStatusIndicator status={5} />
                <div className='tw-text-s tw-font-medium'>
                  {i18nTCommon('shared.loading...')}
                </div>
              </div>
            )}
          </div>
          <div ref={endOfListRef} />
        </div>
      )}
    </>
  );
}

BookingSidePanelDetailsTab.propTypes = {
  booker: resourceShape('simpleUser'),
  bookingGroupInfoBanner: PropTypes.node,
  course: resourceShape('course'),
  domainBookingSource: resourceShape(['booking', 'bookingGroup']),
  domainBookingSourcePersonnelCollection: PropTypes.arrayOf(
    resourceShape('personnel'),
  ),
  endOfListRef: PropTypes.func,
  isFetching: PropTypes.bool,
  shouldShowPersonnel: PropTypes.bool,
  totalReadableBookingCount: PropTypes.number,
};
